/**
 * Performance Inputs Component
 *
 * Input fields for workload type and read percentage with compact design.
 */

import { Box, Slider, Typography, ToggleButtonGroup, ToggleButton } from '@mui/material';
import type { PerformanceInputs } from '../../types';
import { TooltipIcon } from '../ui';

interface PerformanceInputsProps {
  value: PerformanceInputs;
  onChange: (value: PerformanceInputs) => void;
}

export function PerformanceInputsInput({ value, onChange }: PerformanceInputsProps) {
  const handleWorkloadChange = (_: React.MouseEvent<HTMLElement>, newVal: string) => {
    if (newVal) {
      onChange({ ...value, workloadType: newVal as PerformanceInputs['workloadType'] });
    }
  };

  const handleReadPercentageChange = (_event: Event, newValue: number | number[]) => {
    onChange({ ...value, readPercentage: (newValue as number) / 100 });
  };

  const workloadPresets: Record<PerformanceInputs['workloadType'], { read: number; description: string }> = {
    database: { read: 0.70, description: '70% reads - High random IOPS' },
    media: { read: 0.90, description: '90% reads - Sequential workload' },
    mixed: { read: 0.50, description: '50% reads - Balanced workload' },
    backup: { read: 0.30, description: '30% reads - Write-heavy' },
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {/* Section Header */}
      <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: 0.5, color: 'text.secondary' }}>
        Performance Profile
      </Typography>

      {/* Workload Type - Toggle Buttons */}
      <Box>
        <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
          Workload Type
          <TooltipIcon tooltipKey="workloadType" />
        </Typography>
        <ToggleButtonGroup
          value={value.workloadType}
          exclusive
          onChange={handleWorkloadChange}
          size="small"
          fullWidth
          sx={{ height: 28 }}
        >
          <ToggleButton value="database" sx={{ fontSize: '0.7rem', py: 0.5, px: 1 }}>
            Database
          </ToggleButton>
          <ToggleButton value="media" sx={{ fontSize: '0.7rem', py: 0.5, px: 1 }}>
            Media
          </ToggleButton>
          <ToggleButton value="mixed" sx={{ fontSize: '0.7rem', py: 0.5, px: 1 }}>
            Mixed
          </ToggleButton>
          <ToggleButton value="backup" sx={{ fontSize: '0.7rem', py: 0.5, px: 1 }}>
            Backup
          </ToggleButton>
        </ToggleButtonGroup>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'block', mt: 0.5 }}>
          {workloadPresets[value.workloadType].description}
        </Typography>
      </Box>

      {/* Read Percentage Slider - Compact */}
      <Box>
        <Typography variant="caption" sx={{ fontSize: '0.7rem', display: 'block', mb: 0.5 }}>
          Read: <strong>{Math.round(value.readPercentage * 100)}%</strong> / Write: <strong>{Math.round((1 - value.readPercentage) * 100)}%</strong>
          <TooltipIcon tooltipKey="readPercentage" />
        </Typography>
        <Slider
          value={value.readPercentage * 100}
          onChange={handleReadPercentageChange}
          size="small"
          step={5}
          marks={[
            { value: 0, label: '0%' },
            { value: 50, label: '50%' },
            { value: 100, label: '100%' },
          ]}
          valueLabelDisplay="auto"
          sx={{
            height: 4,
            '& .MuiSlider-thumb': { width: 12, height: 12 },
          }}
        />
      </Box>
    </Box>
  );
}
